import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { requestOpenChat } from '../chat/chatLinks';
import { useAgentWhatsappNumber } from '../../hooks/useAgentWhatsappNumber';
import {
  cascadeJobRoutineState,
  integrationPollingInterval,
  isJobOn,
  nextJobActiveState,
  shouldContinueIntegrationPolling,
  type RoutineSchedule,
} from './mondayIntegrationLogic';
import {
  ArrowLeftIcon,
  ChatIcon,
  CheckIcon,
  ChevronRightIcon,
  MinusCircleIcon,
  SparkleIcon,
} from './integrationPanelIcons';
import type {
  IntegrationJob,
  IntegrationJobEntry,
  IntegrationResponse,
  IntegrationRoutine,
} from './integrationPanelTypes';
import { IntegrationJobCard } from './IntegrationJobCard';
import { IntegrationJobModal } from './IntegrationJobModal';
import DisconnectAppModal from './DisconnectAppModal';
import type { DisconnectTarget } from './connectedAppDisconnect';
import {
  providerClass,
  providerPath,
  type IntegrationProviderConfig,
} from './integrationProvider';
import './integration-panel.scss';

export interface IntegrationPanelProps {
  provider: IntegrationProviderConfig;
  connected: boolean;
  onConnect: () => void;
  onDisconnect: (
    oauthId: number,
    deleteLearnedData: boolean,
  ) => Promise<void> | void;
  /** Provider-specific consequences appended to the disconnect warning. */
  disconnectWarning: string;
  installationStep?: {
    heading: string;
    description: string;
    actionLabel: string;
    url: string;
  };
  onBack: () => void;
  accounts: { id: number; email: string; scope: string }[];
  assistantName: string;
}

const EMPTY_RESPONSE: IntegrationResponse = {
  understanding: null,
  jobs: [],
  notes: [],
};

const fallbackProgressSteps = (provider: IntegrationProviderConfig) => [
  `Connecting to your ${provider.displayLabel} account`,
  `Reading your Workspaces & Boards`,
  'Mapping columns, owners & deliverables',
  'Working out how you use each Board',
];

const IntegrationPanel: FC<IntegrationPanelProps> = ({
  provider,
  connected,
  onConnect,
  onDisconnect,
  disconnectWarning,
  installationStep,
  onBack,
  accounts,
  assistantName,
}) => {
  const ProviderLogo = provider.logo;
  const panelClass = (name: string): string => providerClass(provider, name);
  const { getAccessTokenSilently } = useAuth0();
  const { whatsappLink } = useAgentWhatsappNumber();
  const [data, setData] = useState<IntegrationResponse>(EMPTY_RESPONSE);
  const [loading, setLoading] = useState(connected);
  const [message, setMessage] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [understandingOpen, setUnderstandingOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionSent, setCorrectionSent] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [editing, setEditing] = useState<IntegrationJob | null>(null);
  const [guidance, setGuidance] = useState('');
  const [rewriting, setRewriting] = useState(false);
  const [routineState, setRoutineState] = useState<Record<string, boolean>>({});
  const [jobState, setJobState] = useState<Record<string, boolean>>({});
  const [openSchedule, setOpenSchedule] = useState<string | null>(null);
  const [pollingNonce, setPollingNonce] = useState(0);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [disconnectTarget, setDisconnectTarget] =
    useState<DisconnectTarget | null>(null);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const response = await api.get<IntegrationResponse>(
      providerPath(provider),
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setData(response.data);
    setRoutineState((previous) => {
      const next = { ...previous };
      response.data.jobs.forEach(({ routines }) =>
        routines.forEach((routine) => {
          next[routine.id] = routine.active;
        }),
      );
      return next;
    });
    setJobState((previous) => {
      const next = { ...previous };
      response.data.jobs.forEach(({ job, routines }) => {
        next[job.id] = isJobOn(routines, previous);
      });
      return next;
    });
    return response.data;
  }, [getAccessTokenSilently, provider]);

  const openDisconnectModal = (account: { id: number; email: string }) =>
    setDisconnectTarget({
      oauthId: account.id,
      label: account.email,
      providerLabel: provider.displayLabel,
      extraWarning: disconnectWarning,
      run: async (deleteLearnedData) => {
        await onDisconnect(account.id, deleteLearnedData);
      },
    });

  useEffect(() => {
    if (!connected) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let timeoutId: number | undefined;
    const pollingStartedAt = Date.now();
    setPollingTimedOut(false);
    const poll = async () => {
      try {
        const response = await load();
        if (cancelled) return;
        const elapsedMs = Date.now() - pollingStartedAt;
        if (shouldContinueIntegrationPolling(response, elapsedMs)) {
          timeoutId = window.setTimeout(
            poll,
            integrationPollingInterval(elapsedMs),
          );
        } else {
          if (
            response.understanding?.status === 'analyzing' ||
            (response.understanding?.status === 'ready' &&
              response.jobs.length === 0)
          ) {
            setPollingTimedOut(true);
            setMessage(
              `This is taking longer than expected. Please try again in a few minutes.`,
            );
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPollingTimedOut(true);
          setMessage(
            `Unable to load your ${provider.displayLabel} understanding.`,
          );
          setLoading(false);
        }
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [connected, load, pollingNonce]);

  useEffect(() => {
    if (!openSchedule) return undefined;
    const close = () => setOpenSchedule(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSchedule]);

  useEffect(() => {
    if (!editing || rewriting) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setEditing(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editing, rewriting]);

  const understanding = data.understanding;
  const status = understanding?.status ?? 'analyzing';
  const progressSteps = understanding?.progressSteps.length
    ? understanding.progressSteps
    : fallbackProgressSteps(provider);

  const setJobRoutines = (jobEntry: IntegrationJobEntry, active: boolean) => {
    setData((previous) => ({
      ...previous,
      jobs: previous.jobs.map((entry) =>
        entry.job.id === jobEntry.job.id
          ? {
              ...entry,
              routines: entry.routines.map((routine) => ({
                ...routine,
                active,
              })),
            }
          : entry,
      ),
    }));
    setRoutineState((previous) => {
      return {
        ...previous,
        ...cascadeJobRoutineState(jobEntry.routines, active),
      };
    });
    setJobState((previous) => ({ ...previous, [jobEntry.job.id]: active }));
  };

  const toggleJob = async (entry: IntegrationJobEntry) => {
    const active = nextJobActiveState(entry.routines, routineState);
    setJobRoutines(entry, active);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/integration-understanding/jobs/${entry.job.id}/toggle`,
        { active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setJobRoutines(entry, !active);
      setMessage('That job could not be updated. Please try again.');
    }
  };

  const toggleRoutine = async (routine: IntegrationRoutine) => {
    const previous = routineState[routine.id] ?? routine.active;
    const active = !previous;
    setRoutineState((state) => ({ ...state, [routine.id]: active }));
    const routineJob = data.jobs.find((entry) =>
      entry.routines.some((item) => item.id === routine.id),
    );
    if (routineJob) {
      setJobState((state) => {
        const nextRoutineState = { ...state, [routine.id]: active };
        return {
          ...state,
          [routineJob.job.id]: isJobOn(routineJob.routines, nextRoutineState),
        };
      });
    }
    setData((previousData) => ({
      ...previousData,
      jobs: previousData.jobs.map((entry) => ({
        ...entry,
        routines: entry.routines.map((item) =>
          item.id === routine.id ? { ...item, active } : item,
        ),
      })),
    }));
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/integration-understanding/routines/${routine.id}/toggle`,
        { active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setRoutineState((state) => ({ ...state, [routine.id]: previous }));
      if (routineJob) {
        setJobState((state) => {
          const nextRoutineState = { ...state, [routine.id]: previous };
          return {
            ...state,
            [routineJob.job.id]: isJobOn(routineJob.routines, nextRoutineState),
          };
        });
      }
      setMessage('That routine could not be updated. Please try again.');
    }
  };

  const patchSchedule = async (
    routine: IntegrationRoutine,
    schedule: RoutineSchedule,
  ) => {
    setOpenSchedule(null);
    const previous = routine.schedule;
    setData((previousData) => ({
      ...previousData,
      jobs: previousData.jobs.map((entry) => ({
        ...entry,
        routines: entry.routines.map((item) =>
          item.id === routine.id ? { ...item, schedule } : item,
        ),
      })),
    }));
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/integration-understanding/routines/${routine.id}/schedule`,
        { schedule },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setData((previousData) => ({
        ...previousData,
        jobs: previousData.jobs.map((entry) => ({
          ...entry,
          routines: entry.routines.map((item) =>
            item.id === routine.id ? { ...item, schedule: previous } : item,
          ),
        })),
      }));
      setMessage('That schedule could not be updated. Please try again.');
    }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    setMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        providerPath(provider, '/notes'),
        { content: note.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNote('');
      setCorrectionOpen(false);
      setCorrectionSent(true);
      setPollingNonce((value) => value + 1);
    } catch {
      setMessage('We could not save that correction. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const retry = async () => {
    setLoading(true);
    setPollingTimedOut(false);
    setMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        providerPath(provider, '/analyse'),
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPollingNonce((value) => value + 1);
    } catch {
      setLoading(false);
      setMessage('We could not restart the analysis. Please try again.');
    }
  };

  const rewriteJob = async () => {
    if (!editing || !guidance.trim()) return;
    setRewriting(true);
    setMessage(null);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/integration-understanding/jobs/${editing.id}/regenerate`,
        { guidance: guidance.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEditing(null);
      setGuidance('');
      await load();
    } catch {
      setMessage('The job could not be rewritten. Please try again.');
    } finally {
      setRewriting(false);
    }
  };

  const revertJob = async (job: IntegrationJob) => {
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        `/integration-understanding/jobs/${job.id}/revert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await load();
    } catch {
      setMessage('The job could not be reverted. Please try again.');
    }
  };

  const renderAccountRows = (
    <div className='ca-account-list'>
      {accounts.map((account) => (
        <div className='ca-account-row' key={account.id}>
          <div className='ca-account-info'>
            <span className='ca-account-icon'>
              <ProviderLogo />
            </span>
            <span className='ca-account-label'>{account.email}</span>
            <span className='ca-account-scope'>Full workspace access</span>
          </div>
          <button
            className='ca-remove-btn'
            onClick={() => openDisconnectModal(account)}
            aria-label={provider.accessibleLabels.disconnect}
            title={provider.accessibleLabels.disconnect}
          >
            <MinusCircleIcon />
          </button>
        </div>
      ))}
    </div>
  );

  const renderDisconnectModal = (
    <DisconnectAppModal
      target={disconnectTarget}
      onClose={() => setDisconnectTarget(null)}
      assistantName={assistantName}
    />
  );

  const renderProgress = (
    <ol className={panelClass('progress-list')}>
      {progressSteps.map((step, index) => {
        const done = index < (understanding?.progressStep ?? 0);
        const active = index === (understanding?.progressStep ?? 0);
        return (
          <li key={step} className={done ? 'done' : active ? 'active' : ''}>
            <span className={panelClass('step-icon')}>
              {done ? <CheckIcon /> : null}
            </span>
            <span>{step}</span>
          </li>
        );
      })}
    </ol>
  );

  const renderHeader = (
    <div className='ca-detail-header'>
      <button className='ca-back-btn' onClick={onBack}>
        <ArrowLeftIcon />
        <span>Back</span>
      </button>
      <h2 className='ca-detail-title'>{`Connect ${provider.displayLabel}`}</h2>
    </div>
  );

  if (!connected) {
    const connectRow = (
      <div className='ca-connect-row'>
        <button className='ca-connect-btn' onClick={onConnect}>
          <ProviderLogo />
          <span>{`Connect ${provider.displayLabel}`}</span>
        </button>
        <p className='ca-connect-hint'>
          We'll ask for read access to your Workspaces, Boards and Items so{' '}
          {assistantName} can see what you're tracking and who owns what.
        </p>
      </div>
    );

    return (
      <div className={provider.panelClassName}>
        {renderHeader}
        <div className='ca-detail-card'>
          {installationStep ? (
            <ol className='integration-connection-steps'>
              <li className='integration-connection-step'>
                <div className='integration-connection-step-content'>
                  <h3>{installationStep.heading}</h3>
                  <p>{installationStep.description}</p>
                  <a
                    className='ca-connect-btn'
                    href={installationStep.url}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <span>{installationStep.actionLabel}</span>
                  </a>
                </div>
              </li>
              <li className='integration-connection-step'>
                <div className='integration-connection-step-content'>
                  <h3>{`Connect your ${provider.displayLabel} account`}</h3>
                  {connectRow}
                </div>
              </li>
            </ol>
          ) : (
            connectRow
          )}
        </div>
        {renderDisconnectModal}
      </div>
    );
  }

  if (loading || status === 'analyzing' || pollingTimedOut) {
    return (
      <div className={provider.panelClassName}>
        {renderHeader}
        <div className='ca-detail-card'>
          {renderAccountRows}
          <div className={panelClass('analysis-copy')}>
            <h3>{`Understanding your ${provider.displayLabel}…`}</h3>
            <p>
              {assistantName} is reading through your workspaces to see how you
              use them. This usually takes under a minute.
            </p>
          </div>
          {!pollingTimedOut && renderProgress}
          {message && (
            <p className={`${provider.messageClassName} error`}>{message}</p>
          )}
          {pollingTimedOut && (
            <button className={panelClass('primary-button')} onClick={retry}>
              Try again
            </button>
          )}
        </div>
        {renderDisconnectModal}
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className={provider.panelClassName}>
        {renderHeader}
        <div className={`ca-detail-card ${provider.failedClassName}`}>
          {renderAccountRows}
          <h3>{`We couldn't understand your ${provider.displayLabel}`}</h3>
          <p>
            {understanding?.error ||
              'Something went wrong while reading your account.'}
          </p>
          <button className={panelClass('primary-button')} onClick={retry}>
            Try again
          </button>
        </div>
        {renderDisconnectModal}
      </div>
    );
  }

  return (
    <div className={`${provider.panelClassName} ${provider.readyClassName}`}>
      {renderHeader}
      <div className='ca-detail-card'>
        {renderAccountRows}
        <div className={panelClass('result')}>
          <span className={panelClass('result-check')}>
            <CheckIcon />
          </span>
          <div className={panelClass('result-title')}>
            <h3>{`${assistantName}'s read your ${provider.displayLabel}`}</h3>
          </div>
          <button
            type='button'
            className={panelClass('disclosure')}
            aria-expanded={understandingOpen}
            onClick={() => setUnderstandingOpen((open) => !open)}
          >
            What they understood <ChevronRightIcon />
          </button>
        </div>

        <div className={panelClass('stats')}>
          {(understanding?.figures ?? []).map((figure) => (
            <div className={panelClass('figure')} key={figure.label}>
              <strong>
                {figure.value}
                {figure.unit && <small>{figure.unit}</small>}
              </strong>
              <span>{figure.label}</span>
            </div>
          ))}
        </div>

        {understandingOpen && (
          <div className={panelClass('understanding-frame')}>
            <span className={panelClass('generated-tag')}>
              <SparkleIcon /> {`Generated from your ${provider.displayLabel}`}
            </span>
            <p className={panelClass('headline')}>{understanding?.headline}</p>
            <p className={panelClass('scope-line')}>
              {understanding?.scopeLine}
            </p>
            <p className={panelClass('ask-line')}>
              You can now ask them anything about this — try{' '}
              <em>"{understanding?.askExample}"</em>
            </p>
            <button
              type='button'
              className={panelClass('correction-toggle')}
              onClick={() => {
                setCorrectionSent(false);
                setCorrectionOpen(true);
              }}
            >
              Not quite right? Tell {assistantName} what's different
            </button>
            {correctionOpen ? (
              <>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={
                    'e.g. "Product" is actually shared across two teams, and I don\'t manage the Sales workspace day to day'
                  }
                  aria-label='Correction'
                />
                <div className={panelClass('correction-actions')}>
                  <button
                    className={panelClass('primary-button')}
                    onClick={submitNote}
                    disabled={savingNote || !note.trim()}
                  >
                    {savingNote ? 'Sending…' : `Send to ${assistantName}`}
                  </button>
                  <button
                    className={panelClass('text-button')}
                    onClick={() => {
                      setCorrectionOpen(false);
                      setNote('');
                    }}
                    disabled={savingNote}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : correctionSent ? (
              <p className={panelClass('correction-ack')}>
                {`Got it — ${assistantName} will take another look and update this next time they check your ${provider.displayLabel}.`}
              </p>
            ) : null}
          </div>
        )}

        <section className={panelClass('outcomes')}>
          <h3 className='ca-block-title'>What jobs they're doing</h3>
          <p className='ca-block-description'>
            Each job they've taken on, and how they say they'll do it. Change
            how a job gets done and they'll rewrite their side of it.
          </p>
          <div className={panelClass('jobs')}>
            {data.jobs.map((entry, index) => {
              const jobOn =
                jobState[entry.job.id] ?? isJobOn(entry.routines, routineState);
              return (
                <IntegrationJobCard
                  key={entry.job.id}
                  entry={entry}
                  index={index}
                  provider={provider}
                  jobOn={jobOn}
                  routineState={routineState}
                  openSchedule={openSchedule}
                  onToggleJob={() => toggleJob(entry)}
                  onToggleRoutine={toggleRoutine}
                  onToggleSchedule={(routineId) =>
                    setOpenSchedule(
                      openSchedule === routineId ? null : routineId,
                    )
                  }
                  onPatchSchedule={patchSchedule}
                  onEditJob={(job) => {
                    setEditing(job);
                    setGuidance('');
                  }}
                  onRevertJob={revertJob}
                />
              );
            })}
          </div>
        </section>

        <div className={panelClass('bottom-cta')}>
          <button
            type='button'
            className={panelClass('primary-button')}
            onClick={() =>
              requestOpenChat(`${provider.displayLabel} integration`)
            }
          >
            <ChatIcon /> Tell {assistantName} what you need
          </button>
          <p>
            They're not limited to what's listed here — ask for anything, or
            message them on{' '}
            {whatsappLink ? (
              <a href={whatsappLink} target='_blank' rel='noreferrer'>
                WhatsApp
              </a>
            ) : (
              <span>WhatsApp</span>
            )}
            .
          </p>
        </div>
        {message && (
          <p className={`${provider.messageClassName} error`}>{message}</p>
        )}
      </div>

      {editing && (
        <IntegrationJobModal
          editing={editing}
          provider={provider}
          guidance={guidance}
          rewriting={rewriting}
          onGuidanceChange={setGuidance}
          onRewrite={rewriteJob}
          onClose={() => {
            if (!rewriting) setEditing(null);
          }}
        />
      )}
      {renderDisconnectModal}
    </div>
  );
};

export default IntegrationPanel;
